import React from 'react';

interface NonNegativeNumberInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  min?: number | string;
  max?: number | string;
  value?: string | readonly string[] | number;
  defaultValue?: string | readonly string[] | number;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

const numericFloor = (min: NonNegativeNumberInputProps['min']): number => {
  const parsedMin = Number(min);
  return Number.isFinite(parsedMin) ? Math.max(0, parsedMin) : 0;
};

const numericCeiling = (
  max: NonNegativeNumberInputProps['max'],
  floor: number,
): number | null => {
  if (
    max === null ||
    max === undefined ||
    (typeof max === 'string' && max.trim() === '')
  ) {
    return null;
  }

  const parsedMax = Number(max);
  return Number.isFinite(parsedMax) ? Math.max(floor, parsedMax) : null;
};

const clampValue = (
  value: NonNegativeNumberInputProps['value'],
  floor: number,
  ceiling: number | null,
): NonNegativeNumberInputProps['value'] => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return value;
    if (value < 0) return floor;
    if (ceiling !== null && value > ceiling) return ceiling;
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return value;
    if (parsedValue < 0) return String(floor);
    if (ceiling !== null && parsedValue > ceiling) return String(ceiling);
  }

  return value;
};

export const NonNegativeNumberInput = React.forwardRef<
  HTMLInputElement,
  NonNegativeNumberInputProps
>(
  (
    {
      min = 0,
      max,
      value,
      defaultValue,
      onChange,
      onKeyDown,
      onBlur,
      ...inputProps
    },
    ref,
  ) => {
    const floor = numericFloor(min);
    const ceiling = numericCeiling(max, floor);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.currentTarget.value;
      const parsedValue = Number(rawValue);

      if (rawValue.trim() !== '' && Number.isFinite(parsedValue)) {
        if (parsedValue < 0) {
          event.currentTarget.value = String(floor);
        } else if (ceiling !== null && parsedValue > ceiling) {
          event.currentTarget.value = String(ceiling);
        }
      }

      onChange?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (['-', '+', 'e', 'E'].includes(event.key)) {
        event.preventDefault();
      }

      onKeyDown?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const rawValue = event.currentTarget.value;
      const parsedValue = Number(rawValue);
      let normalizedValue: number | null = null;

      if (rawValue.trim() !== '' && Number.isFinite(parsedValue)) {
        if (parsedValue < floor) {
          normalizedValue = floor;
        } else if (ceiling !== null && parsedValue > ceiling) {
          normalizedValue = ceiling;
        }
      }

      if (normalizedValue !== null) {
        event.currentTarget.value = String(normalizedValue);
        onChange?.(event);
      }

      onBlur?.(event);
    };

    return (
      <input
        {...inputProps}
        ref={ref}
        type="number"
        min={floor}
        max={ceiling ?? undefined}
        value={clampValue(value, floor, ceiling)}
        defaultValue={clampValue(defaultValue, floor, ceiling)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    );
  },
);

NonNegativeNumberInput.displayName = 'NonNegativeNumberInput';
